import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { DestroyableComponent } from '@models/destroyable.component';
import {
  BffGetUserCategoriesEndpoint,
  BffGetUserCollectionsEndpoint,
  BffGetUserLessonsEndpoint,
  BffGetUserProfileEndpoint,
  BffGetUserSchoolsEndpoint,
  BffGetUserServicesEndpoint,
  BffGetUserServicesV2Endpoint,
} from 'lingo2-api-models';
import { IPagination } from 'lingo2-models';
import { map, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export type UserProfileInfo = BffGetUserProfileEndpoint.Response['data'];
export type TeachingSubject = BffGetUserProfileEndpoint.Response['data']['teachingSubjects'][0];
export type WorkExperience = BffGetUserProfileEndpoint.Response['data']['workExperiences'][0];
export type EducationSchool = BffGetUserProfileEndpoint.Response['data']['educationSchools'][0];
export type UserServiceBff = BffGetUserServicesEndpoint.Response['data'][0];
export type UserLesson = BffGetUserLessonsEndpoint.Response['data'][0];
export type UserCollectionOfLessons = BffGetUserCollectionsEndpoint.Response['data'][0];
export type UserCollectionOfLessonsItem = UserCollectionOfLessons['items'][0];
export type UserSchool = BffGetUserSchoolsEndpoint.Response['data'][0];
export type UserSchoolParticipant = UserSchool['students'][0];

export interface bffUserServiceBlock {
  items: UserServiceBff[];
  loading: boolean;
  loaded: boolean;
  filter: any;
  pagination: {
    page: number;
    pageSize: number;
  };
}

export interface bffSchoolsBlock {
  items: UserSchool[];
  loading: boolean;
  loaded: boolean;
  filter: any;
  pagination: {
    page: number;
    pageSize: number;
    totalPages?: number;
    total?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileBffService extends DestroyableComponent {
  protected http = inject(HttpClient);

  private readonly bffUrl = environment.bff_url;

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Произошла ошибка при запросе к серверу';

    if (error.error instanceof ErrorEvent) {
      // Ошибка на стороне клиента
      errorMessage = `Ошибка: ${error.error.message}`;
    } else {
      // Ошибка на стороне сервера
      errorMessage = `Код ошибки: ${error.status}, сообщение: ${error.message}`;
    }

    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  public getUserProfileInfo(params: BffGetUserProfileEndpoint.Query): Observable<UserProfileInfo> {
    return this.http[BffGetUserProfileEndpoint.method]<BffGetUserProfileEndpoint.Response>(
      `${this.bffUrl}/${BffGetUserProfileEndpoint.path}`,
      { observe: 'body', params },
    ).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  public getUserCollectionOfLessons(
    params: BffGetUserCollectionsEndpoint.Query,
  ): Observable<BffGetUserCollectionsEndpoint.Response> {
    return this.http[BffGetUserCollectionsEndpoint.method]<BffGetUserCollectionsEndpoint.Response>(
      `${this.bffUrl}/${BffGetUserCollectionsEndpoint.path}`,
      { observe: 'body', params },
    ).pipe(catchError(this.handleError));
  }

  public getUserSchools(params: BffGetUserSchoolsEndpoint.Query): Observable<BffGetUserSchoolsEndpoint.Response> {
    return this.http[BffGetUserSchoolsEndpoint.method]<BffGetUserSchoolsEndpoint.Response>(
      `${this.bffUrl}/${BffGetUserSchoolsEndpoint.path}`,
      { observe: 'body', params },
    ).pipe(catchError(this.handleError));
  }
}
